import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { rentalsService } from '../services/api';
import { AppNav } from '../components/AppNav';

interface DashPeriod {
  id: string;
  unit_name: string;
  tenant_name: string;
  amount: number;
  due_date: string;
  days_late?: number;
  lease_id?: string;
}

interface Dashboard {
  month: string;
  active_leases: number;
  collected: number;
  to_collect: number;
  overdue_count: number;
  upcoming: DashPeriod[];
  overdue: DashPeriod[];
}

const mxn = (n: number) => `$${n.toLocaleString('es-MX')}`;

export default function RentalsDashboardPage() {
  const [dash, setDash] = useState<Dashboard | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    rentalsService
      .getDashboard()
      .then(setDash)
      .catch(() => setError('No se pudo cargar el dashboard de rentas.'));
  }, []);

  // Gate R1: los returns tempranos también llevan AppNav (antes el usuario
  // quedaba atrapado en "No se pudo cargar…" sin Volver ni Salir).
  if (error) {
    return (
      <div className="min-h-[100dvh] bg-background">
        <AppNav />
        <div className="p-8 text-sm text-destructive">{error}</div>
      </div>
    );
  }
  if (!dash) {
    return (
      <div className="min-h-[100dvh] bg-background">
        <AppNav />
        <div className="p-8 text-sm text-muted-foreground">Cargando…</div>
      </div>
    );
  }

  const kpis = [
    { label: 'Activos', value: String(dash.active_leases) },
    { label: 'Cobrado', value: mxn(dash.collected) },
    { label: 'Por cobrar', value: mxn(dash.to_collect) },
    { label: 'Morosos', value: String(dash.overdue_count) },
  ];

  return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Rentas — {dash.month}</h1>
          <div className="flex gap-2">
            <Link to="/admin/rentals/leases" className="text-sm text-primary hover:underline">
              Arrendamientos
            </Link>
            <Link to="/admin/rentals/tenants" className="text-sm text-primary hover:underline">
              Inquilinos
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {kpis.map(({ label, value }) => (
            <div key={label} className="border border-border rounded-xl px-4 py-3 bg-card">
              <p className="text-xl font-semibold">{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        <section className="mb-8">
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3">
            Próximos vencimientos (7 días)
          </h2>
          {dash.upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin vencimientos próximos.</p>
          ) : (
            <div className="space-y-2">
              {dash.upcoming.map((p) => (
                <div key={p.id} className="flex items-center justify-between border border-border rounded-xl px-4 py-3 bg-card text-sm">
                  <span>{p.unit_name} · {p.tenant_name}</span>
                  <span className="text-muted-foreground">{mxn(p.amount)} · vence {p.due_date}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3">
            Morosos
          </h2>
          {dash.overdue.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin morosos.</p>
          ) : (
            <div className="space-y-2">
              {dash.overdue.map((p) => (
                <div key={p.id} className="flex items-center justify-between border border-destructive/30 rounded-xl px-4 py-3 bg-destructive/5 text-sm">
                  <span>{p.unit_name} · {p.tenant_name}</span>
                  <span className="text-destructive font-medium">{mxn(p.amount)} · {p.days_late} días</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
